import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { ArticleBody } from "../components/ArticleBody";
import { PageChrome } from "../components/PageChrome";
import { articleBySlug, articlePath } from "../lib/content";
import {
  articleJsonLd,
  articleOgImage,
  breadcrumbJsonLd,
  canonical,
  documentHeaders,
  faqJsonLd,
  socialMeta,
} from "../lib/seo";

/**
 * `/blog/<slug>` — one article.
 *
 * The article is looked up synchronously from the static corpus rather than
 * loaded: see `lib/content/index.ts` for why the whole corpus is one chunk at
 * this size, and where the seam is when it stops being.
 *
 * An unknown slug is a 404 rather than a redirect to the index. A slug is
 * either an article we published or it is not, and quietly answering 200 with
 * something else is how a site accumulates soft 404s — pages that look fine to
 * a person and read as thin duplicates to a crawler.
 */
export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const article = articleBySlug(params.slug);
    if (!article) throw notFound();
    return article;
  },
  headers: documentHeaders,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const path = articlePath(loaderData.slug);
    return {
      meta: [
        { title: loaderData.title },
        { name: "description", content: loaderData.description },
        ...socialMeta({
          title: loaderData.title,
          description: loaderData.description,
          path,
          lang: "en",
          image: articleOgImage(loaderData.slug),
          imageAlt: loaderData.h1,
        }),
        {
          "script:ld+json": articleJsonLd({
            headline: loaderData.h1,
            description: loaderData.description,
            path,
            published: loaderData.published,
            updated: loaderData.updated,
          }),
        },
        {
          "script:ld+json": breadcrumbJsonLd([
            { name: "Bible Shield", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: loaderData.h1, path },
          ]),
        },
        // Only when the article actually shows the questions on the page —
        // marking up an FAQ a visitor cannot see is the exact thing Google
        // hands out manual actions for.
        ...(loaderData.faq
          ? [{ "script:ld+json": faqJsonLd(loaderData.faq) }]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical(path) }],
    };
  },
  component: ArticlePage,
});

function ArticlePage() {
  const article = Route.useLoaderData();
  return (
    <PageChrome>
      {/* Same column as the article, or it hangs off the left of the measure
          on any screen wider than the text. */}
      <nav
        aria-label="breadcrumb"
        className="mx-auto mb-6 w-full max-w-[68ch] text-sm text-ink2"
      >
        <Link
          to="/blog"
          className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
        >
          blog
        </Link>
      </nav>
      <ArticleBody article={article} />
    </PageChrome>
  );
}
